import React, { useEffect, useRef, useState } from 'react';
import Grid from '@mui/material/Grid';
import Button from 'components/atoms/Button';
import { Pagination } from 'components/molecules/Pagination';
import {
  AccessoriesTableColumns,
  mapAccessoriesToDataSource,
} from 'pages/AncillariesPage/helpers';
import { ERouteLinks } from 'models/route';
import { Table } from 'components/molecules/Table';
import { useTranslations } from 'hooks/useTranslations';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { useAlert } from 'models/alertContext';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ActionTypes } from 'state/actions/alert';
import { CardList } from 'components/molecules/CardList';
import { Loader } from 'components/atoms/Loader';
import { AccessoriesSearchForm } from 'pages/AncillariesPage/AccessoriesPage/AccessoriesSearchForm';
import { AccessoriesCard } from 'pages/AncillariesPage/AccessoriesPage/AccessoriesCard';
import { EPerPages, ISortOptions, ITableDataSource } from 'models/table';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import {
  customFieldsPrepareToSearch,
  objectToQueryString,
  prepareDataWithCustomFieldsToSearch,
  prepareQueryValues,
} from 'helpers/searchQuery';
import {
  IFormElementOptionProp,
  initialAncillariesSearchFormValues,
  ISearchAncillariesFormValues,
} from 'models/form';
import SearchFilterStore from 'state/SearchFilterStore';
import {
  EAncillaryFilterTypes,
  EArtAncillaryExpandType,
  EArtAncillaryType,
  EArtAncillaryTypeName,
} from 'constants/ArtAccessoryType';
import { useQuery } from 'hooks/useQuery';
import {
  ECustomFieldObjectType,
  ECustomFieldType,
  ICustomFieldByObjectType,
} from 'models/customField';
import { useLang } from 'models/langContext';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { EPermission } from 'models/permissions';
import { EChipsVariants } from 'components/atoms/Chips';
import { renderRowColor, renderRowColorHover } from 'components/molecules/Table/helpers';
import { Tooltip } from '@mui/material';
import { isExportNotAvailable } from 'constants/Export';
import { EFirearmStateId } from 'models/firearm';
import { Alert, EAlertVariants, IAlert } from 'components/atoms/Alert';
import { Box } from '@mui/system';
import { ISearchFormCustomFieldValue } from 'pages/LegalEntitiesPage/IndividualSearchForm';
import { usePrivilegesValidation } from 'utils/privilegesValidations';
import { replaceUrlIfOnPage } from 'utils/history';
import { useAncillaries } from '../hooks';
import { joinValuesToString } from 'helpers/joinValuesToString';
import { exportAncillariesCSV } from 'requests/report';
import { EAssetType } from 'models/assetType';

interface AccessoriesPageProps {
  renderDataColor: (data: ITableDataSource) => {
    border: string;
    stateField: EChipsVariants;
    background: string;
  };
  setAccessoryAccordionExpanded: (val: boolean | undefined) => void;
}

const AccessoriesPage = ({
  renderDataColor,
  setAccessoryAccordionExpanded,
}: AccessoriesPageProps) => {
  const { isDesktop } = useThemeBreakpoint();
  const { t } = useTranslations();
  const { pathname }: any = useLocation();
  const navigate = useNavigate();
  const [isAccessoriesLoading, setIsAccessoriesLoading] = useState<boolean>(true);
  const [searchParams] = useSearchParams();
  const urlQuery = new URLSearchParams(searchParams);
  const expandType = urlQuery.get('expand');
  const perPageFromQuery = Number.parseInt(searchParams.get('perPage') || '25', 10);
  const currentPageFromQuery = Number.parseInt(searchParams.get('currentPage') || '1', 10);
  const [currentPageAccessories, setCurrentPageAccessories] = useState<number>(
    expandType === EArtAncillaryExpandType.ACCESSORIES ? currentPageFromQuery : 1,
  );
  const [perPageAccessories, setPerPageAccessories] = useState<EPerPages>(
    expandType === EArtAncillaryExpandType.ACCESSORIES
      ? perPageFromQuery
      : EPerPages.perPage25,
  );
  const [accessoriesSort, setAccessoriesSort] = useState<ISortOptions | null>(null);
  const [accessoriesQuery, setAccessoriesQuery] = useState<
    Partial<ISearchAncillariesFormValues>
  >({});
  const [isAccessoriesQueryLoading, setIsAccessoriesQueryLoading] = useState<boolean>(false);
  const [isSortLoaded, setIsSortLoaded] = useState<boolean>(true);
  const query = useQuery();
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [ownerOptions, setOwnerOptions] = useState<IFormElementOptionProp[]>([]);
  const [keeperOptions, setKeeperOptions] = useState<IFormElementOptionProp[]>([]);
  const [initialValues, setInitialValues] = useState<ISearchAncillariesFormValues | null>(
    null,
  );
  const [areInitialValuesLoaded, setAreInitialValuesLoaded] = useState(false);
  const [areCustomFieldsLoaded, setAreCustomFieldsLoaded] = useState(false);
  const { selectedLanguage } = useLang();
  const { shortDateFormat } = useGlobalProperty();
  const { state: routerState }: any = useLocation();
  const isFromMapQuery: boolean =
    query.get('fromMap') === 'true' || routerState?.accessoriesUniqueIds?.length > 0;
  const storedAccessories = SearchFilterStore.getAccessories();
  const storedMapBounds = SearchFilterStore.getMapBounds();
  const [isExportLoading, setExportLoading] = useState(false);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const { setAlert, clearAlert, accessoryAlert } = useAlert();
  const { isAnyLicenceAuthorityValid } = usePrivilegesValidation();
  const [markedText, setMarkedText] = useState<string | undefined>();

  const scrollPosition = useRef(0);

  const uniqueIds =
    storedAccessories.length > 0
      ? storedAccessories.map((accessoryItem) => accessoryItem.uniqueId)
      : routerState?.accessoriesUniqueIds;

  const {
    paginatorAccessories,
    accessories,
    getAccessoriesData,
    getCustomField,
    customFieldsAccessories,
  } = useAncillaries();

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_NEXT_PAGE_ALERT);
      clearAlert(ActionTypes.CLEAR_ACCESSORY_ALERT);
      setIsAccessoriesLoading(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (isSortLoaded) {
      window.scrollTo({ top: scrollPosition.current, behavior: 'auto' });
      scrollPosition.current = 0;
    }
  }, [isSortLoaded]);

  useEffect(() => {
    (async () => {
      setAreCustomFieldsLoaded(false);
      await getCustomField(ECustomFieldObjectType.AN_ACCESSORY);
      setAreCustomFieldsLoaded(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const previousAccessoriesSortRef = useRef(accessoriesSort);

  useEffect(() => {
    (async function getDataInit() {
      setIsPaginationLoading(true);
      if (!accessories.length) {
        setIsAccessoriesLoading(true);
      }

      if (isFromMapQuery) {
        if (storedMapBounds && storedMapBounds.assetType === EAssetType.ACCESSORY) {
          accessoriesQuery.minLatitude = storedMapBounds.minLatitude;
          accessoriesQuery.maxLatitude = storedMapBounds.maxLatitude;
          accessoriesQuery.minLongitude = storedMapBounds.minLongitude;
          accessoriesQuery.maxLongitude = storedMapBounds.maxLongitude;

          delete accessoriesQuery.uniqueIds;
        } else if (uniqueIds && uniqueIds.length) {
          accessoriesQuery.uniqueIds = uniqueIds;

          delete accessoriesQuery.minLatitude;
          delete accessoriesQuery.maxLatitude;
          delete accessoriesQuery.minLongitude;
          delete accessoriesQuery.maxLongitude;
        } else {
          delete accessoriesQuery.uniqueIds;

          delete accessoriesQuery.minLatitude;
          delete accessoriesQuery.maxLatitude;
          delete accessoriesQuery.minLongitude;
          delete accessoriesQuery.maxLongitude;
        }
      }

      if (accessoriesSort !== previousAccessoriesSortRef.current) {
        setIsSortLoaded(false);
      }
      if (Object.keys(accessoriesQuery).length !== 0) {
        customFieldsPrepareToSearch(accessoriesQuery.customFields);
        const accessoriesQueryData = prepareDataWithCustomFieldsToSearch(
          accessoriesQuery,
          customFieldsAccessories.filter(
            (item: ICustomFieldByObjectType) => item.searchCriteria,
          ),
        );
        await getAccessoriesData(
          currentPageAccessories,
          perPageAccessories,
          {
            ...accessoriesQueryData,
          },
          accessoriesSort,
        );
        setIsAccessoriesLoading(false);
      }

      setIsAccessoriesQueryLoading(false);
      setIsPaginationLoading(false);
      setIsSortLoaded(true);
      previousAccessoriesSortRef.current = accessoriesSort;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessoriesQuery, accessoriesSort, selectedLanguage]);

  useEffect(() => {
    if (initialValues !== undefined && areCustomFieldsLoaded) {
      setAreInitialValuesLoaded(true);
    }
  }, [initialValues, areCustomFieldsLoaded]);

  useEffect(() => {
    if (areCustomFieldsLoaded) {
      setAreInitialValuesLoaded(false);
      if (expandType === EArtAncillaryExpandType.ACCESSORIES) {
        setShowAdvanced(true);
        const queryValues = prepareQueryValues(
          {
            ...initialAncillariesSearchFormValues,
            perPage: perPageAccessories,
            currentPage: currentPageAccessories,
          },
          urlQuery,
          [
            ...customFieldsAccessories
              .filter((cf) => cf.customFieldType === ECustomFieldType.DATE)
              .map((cf) => [`from_${cf.name}`, `to_${cf.name}`]),
          ].flat(),
        );
        setInitialValues({ ...initialAncillariesSearchFormValues, ...queryValues });
      } else {
        setInitialValues(initialAncillariesSearchFormValues);
        setAreInitialValuesLoaded(true);
      }
    }
  }, [searchParams, areCustomFieldsLoaded]);

  const getNewQueryPath = (values: Partial<ISearchAncillariesFormValues>): string => {
    const valuesWithoutPreset = { ...values };
    valuesWithoutPreset.preset = undefined;
    valuesWithoutPreset.formSubmitted = undefined;
    valuesWithoutPreset.perPage = perPageAccessories;
    valuesWithoutPreset.currentPage = currentPageAccessories;

    const valuesToString = {
      ...valuesWithoutPreset,
      accessoryCapacities: joinValuesToString(values.accessoryCapacities),
      calibresUniqueIds: joinValuesToString(values.calibresUniqueIds),
      cartridgeCompositionsUniqueIds: joinValuesToString(
        values.cartridgeCompositionsUniqueIds,
      ),
      coloursOfPrimerSealantUniqueIds: joinValuesToString(
        values.coloursOfPrimerSealantUniqueIds,
      ),
      componentCapacitiesUniqueIds: joinValuesToString(values.componentCapacitiesUniqueIds),
      componentmanufacturersUniqueIds: joinValuesToString(
        values.componentmanufacturersUniqueIds,
      ),
      compositionsUniqueIds: joinValuesToString(values.compositionsUniqueIds),
      factoriesUniqueIds: joinValuesToString(values.factoriesUniqueIds),
      frtMakesUniqueIds: joinValuesToString(values.frtMakesUniqueIds),
      frtManufacturersUniqueIds: joinValuesToString(values.frtManufacturersUniqueIds),
      frtModelsUniqueIds: joinValuesToString(values.frtModelsUniqueIds),
      frtTypesUniqueIds: joinValuesToString(values.frtTypesUniqueIds),
      functionalTypesUniqueIds: joinValuesToString(values.functionalTypesUniqueIds),
      hostTypesUniqueIds: joinValuesToString(values.hostTypesUniqueIds),
      lightColoursUniqueIds: joinValuesToString(values.lightColoursUniqueIds),
      magnificationsUniqueIds: joinValuesToString(values.magnificationsUniqueIds),
      manufacturersUniqueIds: joinValuesToString(values.manufacturersUniqueIds),
      munitionTypesUniqueIds: joinValuesToString(values.munitionTypesUniqueIds),
      munitionVelocitiesUniqueIds: joinValuesToString(values.munitionVelocitiesUniqueIds),
      projectileShapesUniqueIds: joinValuesToString(values.projectileShapesUniqueIds),
      proofHousesUniqueIds: joinValuesToString(values.proofHousesUniqueIds),
      reticlesUniqueIds: joinValuesToString(values.reticlesUniqueIds),
      shotSizesUniqueIds: joinValuesToString(values.shotSizesUniqueIds),
      statesUniqueIds: joinValuesToString(values.statesUniqueIds),
      utilitiesUniqueIds: joinValuesToString(values.utilitiesUniqueIds),
    };

    const objQuery = objectToQueryString(
      valuesToString,
      customFieldsAccessories,
      shortDateFormat,
    );

    if (
      objQuery.length &&
      (!isInitialLoad || expandType === EArtAncillaryExpandType.ACCESSORIES)
    ) {
      setIsInitialLoad(false);
      return `${pathname}?expand=accessories&${objQuery}`;
    }
    if (values.formSubmitted === true) {
      return `${pathname}?expand=accessories`;
    }
    setIsInitialLoad(false);
    return `${pathname}`;
  };

  const areCustomFieldValuesSame = (
    cfValues: ISearchFormCustomFieldValue[] | undefined,
    queryValues: ISearchFormCustomFieldValue[] | undefined,
  ): boolean => {
    if (!cfValues || !queryValues) {
      return true;
    }
    let areSame = true;
    cfValues.forEach((cfValue) => {
      const queryCf = queryValues.find(
        (it) => it.customFieldUniqueId === cfValue.customFieldUniqueId,
      );
      if (
        cfValue.bool !== queryCf?.bool ||
        cfValue.text !== queryCf?.text ||
        cfValue.dateFrom !== queryCf?.dateFrom ||
        cfValue.dateTo !== queryCf?.dateTo
      ) {
        areSame = false;
      }
    });

    return areSame;
  };

  const handleSetAccessoriesQuery = (values: Partial<ISearchAncillariesFormValues>) => {
    if (values.invokedByPageChange !== true) {
      setIsAccessoriesQueryLoading(true);
    }
    values.invokedByPageChange = undefined;
    const valuesWithoutPreset = { ...values };
    valuesWithoutPreset.preset = undefined;
    valuesWithoutPreset.formSubmitted = undefined;

    if (isInitialLoad) {
      setIsInitialLoad(false);
    } else {
      replaceUrlIfOnPage(getNewQueryPath(values));

      if (
        values.ancillaryTypeUniqueId !== accessoriesQuery.ancillaryTypeUniqueId ||
        values.fireCycleId !== accessoriesQuery.fireCycleId ||
        values.serialNumber !== accessoriesQuery.serialNumber ||
        values.exhibitNumber !== accessoriesQuery.exhibitNumber ||
        values.manufacturerUniqueId !== accessoriesQuery.manufacturerUniqueId ||
        values.productName !== accessoriesQuery.productName ||
        values.calibreUniqueId !== accessoriesQuery.calibreUniqueId ||
        values.hostTypeUniqueId !== accessoriesQuery.hostTypeUniqueId ||
        values.magnificationUniqueId !== accessoriesQuery.magnificationUniqueId ||
        values.utilityUniqueId !== accessoriesQuery.utilityUniqueId ||
        values.reticleUniqueId !== accessoriesQuery.reticleUniqueId ||
        values.lightColourUniqueId !== accessoriesQuery.lightColourUniqueId ||
        values.compositionUniqueId !== accessoriesQuery.compositionUniqueId ||
        values.accessoryCapacity !== accessoriesQuery.accessoryCapacity ||
        values.proofHouseUniqueId !== accessoriesQuery.proofHouseUniqueId ||
        values.stateUniqueId !== accessoriesQuery.stateUniqueId ||
        values.legalityUniqueId !== accessoriesQuery.legalityUniqueId ||
        !areCustomFieldValuesSame(values.customFields, accessoriesQuery.customFields) ||
        values.keeperUniqueId !== accessoriesQuery.keeperUniqueId ||
        values.ownerUniqueId !== accessoriesQuery.ownerUniqueId
      ) {
        setCurrentPageAccessories(1);
      }
    }
    setMarkedText(values.marking);

    setAccessoriesQuery(valuesWithoutPreset);
    if (
      routerState?.presetAncillaries &&
      routerState?.ancillaryFilterType === EAncillaryFilterTypes.ACCESSORY
    ) {
      setAccessoryAccordionExpanded(true);
    }
  };

  const exportCsv = async () => {
    setExportLoading(true);
    accessoriesQuery.ancillaryDictionaryUniqueId = EArtAncillaryType.ACCESSORIES;
    await exportAncillariesCSV(accessoriesQuery, accessoriesSort);
    setExportLoading(false);
  };

  const handleRedirectToEditPageButton = (item: ITableDataSource) => {
    if (customFieldsAccessories?.length && item.stateUniqueId !== EFirearmStateId.DISABLED) {
      navigate(ERouteLinks.EditAccessories.replace(':id', item.id as string));
    } else if (item.stateUniqueId === EFirearmStateId.DISABLED) {
      setAlert(ActionTypes.SET_ACCESSORY_ALERT, {
        text: t('ancillary.disabled.edit.info'),
        variant: EAlertVariants.error,
      } as IAlert);
    } else {
      setAlert(ActionTypes.SET_ACCESSORY_ALERT, {
        text: t('general.noAvaibleDataToEdit.label'),
        variant: EAlertVariants.warning,
      } as IAlert);
    }
  };

  return (
    <div>
      {initialValues && areInitialValuesLoaded && areCustomFieldsLoaded && (
        <AccessoriesSearchForm
          ownerOptions={ownerOptions}
          keeperOptions={keeperOptions}
          setOwnerOptions={setOwnerOptions}
          setKeeperOptions={setKeeperOptions}
          showAdvanced={showAdvanced}
          accessoriesUniqueIds={uniqueIds}
          isSearchButtonDisabled={isAccessoriesLoading || isAccessoriesQueryLoading}
          isFromMap={isFromMapQuery}
          returnPath={`${pathname}?expand=accessories`}
          setShowAdvanced={setShowAdvanced}
          onSubmit={handleSetAccessoriesQuery}
          getNewQueryPath={getNewQueryPath}
          initialFormValues={initialValues ?? undefined}
          customFields={customFieldsAccessories.filter(
            (item: ICustomFieldByObjectType) => item.searchCriteria,
          )}
          perPage={perPageAccessories}
          currentPage={currentPageAccessories}
        />
      )}
      <Grid sx={{ display: 'flex', justifyContent: 'flex-end', margin: '10px 0', gap: 1 }}>
        {!isAccessoriesLoading && (
          <>
            {isExportNotAvailable(paginatorAccessories) ? (
              <Tooltip title={t('dataExportRecordsLimitAmount.label')} arrow>
                <span>
                  <Button
                    variant={EButtonVariants.outlined}
                    size={EButtonSizes.medium}
                    permission={[EPermission.EXPORT_ANCILLARIES]}
                    label={t('ancillaries.accordion.export.button')}
                    type="submit"
                    onClick={exportCsv}
                    isLoading={isExportLoading}
                    disabled
                  />
                </span>
              </Tooltip>
            ) : (
              <Button
                variant={EButtonVariants.outlined}
                size={EButtonSizes.medium}
                permission={[EPermission.EXPORT_ANCILLARIES]}
                label={t('ancillaries.accordion.export.button')}
                type="submit"
                onClick={exportCsv}
                isLoading={isExportLoading}
              />
            )}
            <Button
              variant={EButtonVariants.contained}
              size={EButtonSizes.medium}
              label={t('ancillaries.accordion.showOnMap.button')}
              onClick={() => {
                navigate(ERouteLinks.RegisteredAncillariesMap, {
                  state: {
                    ancillaryType: 'accessory',
                    ancillaryQuery: accessoriesQuery,
                    showAdvanced,
                    options: { ownerOptions, keeperOptions },
                    navigateBack: -1,
                  },
                });
              }}
            />
          </>
        )}
      </Grid>
      {isAccessoriesLoading || isAccessoriesQueryLoading ? (
        <Loader isLoading={true} />
      ) : (
        <>
          {isDesktop ? (
            <Table
              columns={AccessoriesTableColumns(
                {
                  handleEdit: handleRedirectToEditPageButton,
                  customFields: customFieldsAccessories,
                  t,
                  isAnyLicenceAuthorityValid,
                  markedText: markedText,
                  showOnMap: (item) => {
                    navigate(ERouteLinks.RegisteredAncillariesMap, {
                      state: {
                        ancillaryUniqueId: item?.id,
                        ancillaryType: EArtAncillaryTypeName.ACCESSORY.toLowerCase(),
                        legalityUniqueId: item?.legalityUniqueId,
                        navigateBack: `${ERouteLinks.Ancillaries}?expand=accessories`,
                        showAdvanced: true,
                        showLegalityBar: false,
                        breadcrumbs: [
                          {
                            label: t('module.ancillaries.name'),
                            route: ERouteLinks.Ancillaries,
                          },
                          {
                            label: t('module.accessories.name'),
                            route: `${ERouteLinks.Ancillaries}?expand=accessories`,
                          },
                          t('ancillaries.ancillariesLocation.showOnMap.label'),
                        ],
                      },
                    });
                  },
                },
                (data: ITableDataSource) => renderDataColor(data).stateField,
                selectedLanguage?.uniqueId,
                shortDateFormat,
              )}
              specificRowColor={renderRowColor}
              specificRowColorHover={renderRowColorHover}
              dataSource={mapAccessoriesToDataSource(accessories)}
              onSort={(it) => {
                setAccessoriesSort(it);
                scrollPosition.current = window.scrollY;
              }}
              isSortLoaded={isSortLoaded}
              customPaddingIfNoData="30px"
            />
          ) : (
            <CardList
              items={mapAccessoriesToDataSource(accessories)}
              render={(data, index) => (
                <AccessoriesCard
                  data={data}
                  key={index}
                  handleView={(id: string) =>
                    navigate(ERouteLinks.ViewAccessories.replace(':id', id))
                  }
                  handleEdit={handleRedirectToEditPageButton}
                  hasCustomFields={!!customFieldsAccessories.length}
                  renderColor={(item) => renderDataColor(item)}
                  isAnyLicenceAuthorityValid={isAnyLicenceAuthorityValid}
                />
              )}
            />
          )}

          {paginatorAccessories && (
            <Pagination
              count={paginatorAccessories?.totalElements}
              perPage={perPageAccessories}
              onChangePage={(page) => {
                setCurrentPageAccessories(page);
              }}
              onChangePerPage={(value) => {
                setPerPageAccessories(value);
              }}
              current={currentPageAccessories}
              isLoading={isPaginationLoading}
              isVisible={isSortLoaded}
            />
          )}
        </>
      )}
      {accessoryAlert && (
        <Box sx={{ marginBottom: '10px', mt: 2 }}>
          <Alert text={accessoryAlert.text} variant={accessoryAlert.variant} />
        </Box>
      )}
    </div>
  );
};

export default AccessoriesPage;
